import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AuthenticationService } from '../auth/auth_service';
import { entities } from '../client/client';
import { ClientService } from '../client/client.service';

@Injectable({
  providedIn: 'root',
})
export class CheckAccessDataService {
  private auth = inject(AuthenticationService);
  private client = inject(ClientService);

  private readonly userRolesKey = 'userRoles';
  private readonly rolesResolved = signal(false);
  private inFlight: Promise<void> | null = null;

  readonly rolesReady = this.rolesResolved.asReadonly();

  readonly roles = computed(() => {
    const jwt = this.auth.jwt();
    if (jwt) {
      return jwt.roles ?? [];
    }
    return this.loadRolesFromStorage();
  });

  constructor() {
    effect(() => {
      const jwt = this.auth.jwt();
      if (jwt) {
        localStorage.setItem(this.userRolesKey, JSON.stringify(jwt.roles ?? []));
      } else if (!this.auth.loggedIn()) {
        localStorage.removeItem(this.userRolesKey);
        this.rolesResolved.set(false);
      }
    });

    if (this.auth.loggedIn()) {
      void this.ensureRolesReady();
    }
  }

  async ensureRolesReady(): Promise<void> {
    if (this.rolesResolved()) {
      return;
    }
    if (!this.auth.loggedIn() || this.auth.jwt()) {
      this.rolesResolved.set(true);
      return;
    }
    this.inFlight ??= this.auth.refreshJwt().finally(() => {
      this.inFlight = null;
    });
    try {
      await this.inFlight;
    } catch {
      // fall through: never block the UI on a token refresh failure
    } finally {
      this.rolesResolved.set(true);
    }
  }

  async GetStaff(): Promise<void> {
    const client = await this.client.getRequestClient();
    await client.accountinternal.GetMyStaff();
    await this.auth.refreshJwt();
  }

  async CreateStaff(dto: entities.CreateStaffRequest): Promise<void> {
    const client = await this.client.getRequestClient();
    await client.accountinternal.CreateMyStaff(dto);
    // Creating the staff record grants roles, so the cached token predates them.
    await this.auth.refreshJwt(true);
  }

  getRolesData(): string[] {
    return this.roles();
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  private loadRolesFromStorage(): string[] {
    const userAuthDataString = localStorage.getItem(this.userRolesKey);
    return userAuthDataString ? JSON.parse(userAuthDataString) : [];
  }
}
